import { DatePipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, EventEmitter, Input, OnInit, Output, ChangeDetectorRef } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { RegExp } from 'src/app/core/config/regex';
import { Context } from 'src/app/shared/models/context';
import { Message, Particular, ProvidentFund, Salary, SalaryParticular } from 'src/app/shared/models/domain';
import { CommonService } from 'src/app/shared/services/common.service';
import { EmployeeService } from 'src/app/shared/services/employee.service';

@Component({
  selector: 'app-salary-crud',
  templateUrl: './salary-crud.component.html',
  styleUrls: ['./salary-crud.component.scss']
})
export class SalaryCrudComponent implements OnInit {
  @Input() salary: Salary;
  @Input() context: Context;
  @Input() salaries: Salary[] = [];
  @Output() passEntry: EventEmitter<any> = new EventEmitter();
  public salaryParticularForm: FormGroup;
  public particulars: Particular[] = [];
  public empcheck: boolean = false;
  public isShown: boolean = false;
  public salaryId: number;
  public frequencies: String[] = [];
  public providentFunds: ProvidentFund[] = [];
  public salaryForm: FormGroup;
  public submitted: boolean = false;
  public submittedParticular: boolean = false;
  public totalSalary: number = 0;
  private modified: boolean = false;

  constructor(
    private fb: FormBuilder,
    public activeModal: NgbActiveModal,
    private commonService: CommonService,
    private employeeService: EmployeeService,
    private toaster: ToastrService,
    private datePipe: DatePipe,
    private cdr: ChangeDetectorRef
  ) {
    this.salaryParticularForm = this.fb.group({
      salaryParticulars: this.fb.array([])
    });
  }

  ngOnInit(): void {
    this.setSalaryForm(this.salary);
    this.employeeService.getFrequency().subscribe(frequencies => {
      this.frequencies = frequencies;
      this.calculateTotalSalary();
      this.cdr.detectChanges();
    });

    this.employeeService.getProvidentFunds(false).subscribe(providentFunds => {
      this.providentFunds = providentFunds;
      this.calculateTotalSalary();
      this.cdr.detectChanges();
    });

    this.employeeService.getParticulars(null, false).subscribe((parti) => {
      this.particulars = parti;
      this.calculateTotalSalary();
      this.cdr.detectChanges();
    });

    if (this.salary?.id) {
      this.salaryId = this.salary.id;
      this.employeeService.getSalaryParticulars(this.salary.id, false).subscribe(salaryParticulars => {
        this.setSalaryParticulars(salaryParticulars);
        this.calculateTotalSalary();
        this.cdr.detectChanges();
      });
    } else {
      this.setSalaryParticulars([]);
    }

    this.salaryForm.valueChanges.subscribe(() => {
      this.modified = true;
      this.calculateTotalSalary();
      this.cdr.detectChanges();
    });

    this.salaryParticularForm.get('salaryParticulars').valueChanges.subscribe(() => {
      this.calculateTotalSalary();
      this.cdr.detectChanges();
    });
  }

  public setSalaryForm(salary?: Salary) {
    if (!salary)
      salary = {
        id: null,
        fromDate: this.datePipe.transform(new Date(), 'yyyy-MM-dd'),
        toDate: null,
        frequency: "MONTHLY",
        active: true,
        disabled: false,
        employeeId: this.context?.employeeId,
        employeeName: null,
        providentFundId: null,
        providentFundName: null,
        basicSalary: null
      } as Salary;
    this.salaryForm = this.fb.group({
      id: this.fb.control(salary.id),
      fromDate: this.fb.control(this.datePipe.transform(salary.fromDate, 'yyyy-MM-dd'), [Validators.required]),
      toDate: this.fb.control(this.datePipe.transform(salary.toDate, 'yyyy-MM-dd'), [Validators.required]),
      providentFundId: this.fb.control(salary.providentFundId),
      providentFundName: this.fb.control(salary.providentFundName, []),
      active: this.fb.control(salary.active),
      basicSalary: this.fb.control(salary.basicSalary, [Validators.required, Validators.pattern(RegExp.basicSalary)]),
      disabled: this.fb.control(salary.disabled),
      frequency: this.fb.control(salary.frequency, [Validators.required]),
      employeeId: this.fb.control(salary.employeeId, [Validators.required]),
      employeeName: this.fb.control(salary.employeeName),
    });
    this.calculateTotalSalary();
    this.cdr.detectChanges();
  }

  get f() {
    return this.salaryForm.controls;
  }

  get p() {
    return this.salaryParticularForm.controls;
  }

  public saveSalary(inProgress?: any) {
    this.submitted = true;
    if (this.salaryForm.valid) {
      const salary = this.salaryForm.value as Salary;
      if (!this.modified) {
        this.activeModal.close(false);
        this.commonService.setToastr(200, `Salary has been updated successfully!`);
        return;
      }
      inProgress.value = true;
      if (salary.id) {
        this.employeeService.updateSalary(salary.id, salary).subscribe({
          next: (response: Message) => {
            if (!response.failed) {
              this.salaryId = salary.id;
              this.passEntry.emit(response);
              this.toaster.success(response.text);
              inProgress.value = false;
            } else {
              inProgress.value = false;
              this.commonService.setToastr(500, 'Unable to complete the action. Please try again.');
            }
            this.submitted = false;
          },
          error: (error: HttpErrorResponse) => {
            inProgress.value = false;
          },
        });
      } else {
        if (!this.empcheck) {
          this.employeeService.createSalary(salary).subscribe({
            next: (response: Message) => {
              if (!response.failed) {
                this.salaryId = parseInt(response.payload);
                this.salaryForm.get("id").setValue(this.salaryId);
                this.passEntry.emit(response);
                this.toaster.success(response.text);
                inProgress.value = false;
              } else {
                inProgress.value = false;
                this.commonService.setToastr(500, 'Unable to complete the action. Please try again.');
              }
              this.submitted = false;
            },
            error: (error: HttpErrorResponse) => {
              inProgress.value = false;
            },
          });
        } else {
          this.toaster.warning("Salary cannot be added");
        }
      }
    }
  }

  get salaryParticulars(): FormArray {
    return this.salaryParticularForm.get("salaryParticulars") as FormArray;
  }

  private setSalaryParticulars(salaryParticulars: SalaryParticular[]) {
    while (this.salaryParticulars.length) {
      this.salaryParticulars.removeAt(0);
    }
    if (salaryParticulars.length > 0) {
      salaryParticulars.forEach(l => this.addSalaryParticular(l));
    }
    this.addSalaryParticular();
    this.calculateTotalSalary();
    this.cdr.detectChanges();
  }

  private addSalaryParticular(salaryParticular?: SalaryParticular) {
    const particularForm = this.fb.group({
      id: this.fb.control(salaryParticular?.id || null, []),
      salaryId: this.fb.control(salaryParticular?.salaryId || this.salaryId, []),
      salaryName: this.fb.control(salaryParticular?.salaryName || "", []),
      particularId: this.fb.control(salaryParticular?.particularId, [Validators.required]),
      value: this.fb.control(salaryParticular?.value || "", [Validators.required]),
      active: this.fb.control(salaryParticular?.active || false, []),
      disabled: this.fb.control(salaryParticular?.disabled || false, []),
    });
    this.salaryParticulars.push(particularForm);
    this.calculateTotalSalary();
    this.cdr.detectChanges();
  }

  public upsertSalaryParticular(salaryParticular: FormGroup, index: number) {
    this.submittedParticular = true;
    if (salaryParticular.valid) {
      let salaryparticular = salaryParticular.value as SalaryParticular;
      salaryparticular.salaryId = this.salaryId;
      if (salaryparticular.id) {
        this.employeeService.updateSalaryParticular(salaryparticular.id, salaryparticular).subscribe({
          next: (message: Message) => {
            if (!message.failed) {
              this.toaster.success(message.text);
              this.calculateTotalSalary();
              this.cdr.detectChanges();
              if (this.salaryParticulars.length <= 1) this.addSalaryParticular();
            } else {
              this.commonService.setToastr(500, 'Unable to complete the action. Please try again.');
            }
            this.submittedParticular = false;
          },
          error: (error: HttpErrorResponse) => {
            this.toaster.error(error?.error?.name || error.message);
            this.submittedParticular = false;
          }
        });
      } else {
        if (!this.empcheck && (this.context?.employeeId || this.salary?.employeeId)) {
          this.employeeService.createSalaryParticular(salaryparticular).subscribe({
            next: (message: Message) => {
              if (!message.failed) {
                this.salaryParticulars.at(index).patchValue({ id: parseInt(message.payload) });
                this.toaster.success(message.text);
                this.calculateTotalSalary();
                this.cdr.detectChanges();
                this.addSalaryParticular();
              } else {
                this.commonService.setToastr(500, 'Unable to complete the action. Please try again.');
              }
              this.submittedParticular = false;
            },
            error: (error: HttpErrorResponse) => {
              this.toaster.error(error?.error?.name || error.message);
              this.submittedParticular = false;
            }
          });
        } else {
          this.toaster.warning("SalaryParticular cannot be added");
          this.submittedParticular = false;
        }
      }
    } else {
      this.submittedParticular = false;
    }
  }

  public deleteSalaryParticular(index: number): void {
    const salaryParticular = this.salaryParticulars.at(index) as FormGroup;
    const salaryParticularValue = salaryParticular.value as SalaryParticular;
    if (salaryParticularValue.id) {
      this.employeeService.deleteSalaryParticular(salaryParticularValue.id).subscribe({
        next: (message: Message) => {
          if (!message.failed) {
            this.salaryParticulars.removeAt(index);
            this.commonService.setToastr(200, message.text || 'Salary particular deleted successfully.');
            this.calculateTotalSalary();
            this.cdr.detectChanges();
            if (this.salaryParticulars.length < 1) {
              this.addSalaryParticular();
            }
          } else {
            this.commonService.setToastr(400, message.text || 'Failed to delete salary particular. Please try again.');
          }
        },
        error: (response: HttpErrorResponse) => {
          console.error('Delete salary particular error:', response);
          this.commonService.setToastr(400, response?.error?.message || 'Failed to delete salary particular. Please try again.');
        }
      });
    } else {
      this.salaryParticulars.removeAt(index);
      this.commonService.setToastr(200, 'Salary particular removed successfully.');
      this.calculateTotalSalary();
      this.cdr.detectChanges();
      if (this.salaryParticulars.length < 1) {
        this.addSalaryParticular();
      }
    }
  }

  private calculateTotalSalary(): void {
    const basicSalary = parseFloat(this.salaryForm.get('basicSalary')?.value) || 0;
    let totalEarnings = 0;
    let totalDeductions = 0;
    let pfDeduction = 0;

    // 1. Calculate Earnings and Deductions from Salary Particulars
    this.salaryParticulars.controls.forEach((control: FormGroup) => {
      const particularId = control.get('particularId')?.value;
      const value = parseFloat(control.get('value')?.value) || 0;
      const particular = this.particulars.find(p => p.id === particularId);

      if (particular && value) {
        if (particular.earnings) {
          totalEarnings += value;
        } else {
          totalDeductions += value;
        }
      }
    });

    // 2. Calculate Provident Fund Deduction
    const providentFundId = this.salaryForm.get('providentFundId')?.value;
    const selectedProvidentFund = this.providentFunds.find(pf => pf.id === providentFundId);

    if (selectedProvidentFund) {
      const employeeRatio = selectedProvidentFund.employeeRatio || 0;
      const pfBaseValue = selectedProvidentFund.value || 0;  // Use providentFund.value, not basic salary

      if (selectedProvidentFund.type === 'FIXED') {
        pfDeduction = employeeRatio;
      } else if (selectedProvidentFund.type === 'PERCENTAGE') {
        pfDeduction = (pfBaseValue * employeeRatio) / 100;
      }
    }

    // 3. Calculate Final Total Salary
    this.totalSalary = (basicSalary + totalEarnings) - (totalDeductions + pfDeduction);
  }
}